/** @jsxImportSource react */
import React, { useState, useRef } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuth } from '../contexts/auth';
import { useTranslation } from '../i18n';
import { Loader2, AlertCircle, Download } from 'lucide-react';
import { UserRole } from '../types';
import { InvoiceTemplate } from '../components/InvoiceTemplate';
import html2pdf from 'html2pdf.js';

const InvoicePage = () => {
    const { id } = useParams<{ id: string }>();
    const { user: currentUser } = useAuth();
    const { t } = useTranslation();
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const invoiceRef = useRef<HTMLDivElement>(null);

    const { data: order, isLoading: isOrderLoading, error: orderError } = useQuery({
        queryKey: ['order', id],
        queryFn: () => api.getOrderById(id!),
        enabled: !!id,
    });

    const { data: client, isLoading: isClientLoading } = useQuery({
        queryKey: ['user', order?.clientId],
        queryFn: () => api.getUsers().then(users => users.find(u => u.id === order!.clientId)),
        enabled: !!order,
    });

    const generatePDF = async () => {
        if (!invoiceRef.current || !order) return;
        
        setIsGeneratingPdf(true);
        const element = invoiceRef.current;
        const opt = {
            margin: 0,
            filename: `Invoice_${order.orderId}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        try {
            await html2pdf().set(opt).from(element).save();
        } catch (error) {
            console.error("PDF Generation Failed", error);
            // Fallback to print
            window.print();
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    // Auto-download on load if not manually suppressed (optional)
    // Keeping this manual to allow preview
    
    if (order && currentUser && currentUser.role !== UserRole.Admin && currentUser.id !== order.clientId) {
        return <Navigate to="/dashboard" />;
    }

    const isLoading = isOrderLoading || isClientLoading;

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-50">
                <div className="text-center">
                    <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary-600" />
                    <p className="mt-4 text-lg font-medium text-secondary-700">{t('loadingInvoice')}</p>
                </div>
            </div>
        );
    }
    
    if (orderError || !order || !client) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-50 p-4">
                 <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md w-full">
                    <AlertCircle className="mx-auto h-16 w-16 text-rose-500 mb-4" />
                    <h2 className="text-2xl font-bold text-secondary-900 mb-2">{t('invoiceError')}</h2>
                    <p className="text-secondary-500">Unable to load invoice details.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8 flex flex-col items-center">
             {/* Control Bar */}
            <div className="w-full max-w-[210mm] mb-6 flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-secondary-200 no-print">
                <div className="flex items-center gap-3">
                    {isGeneratingPdf ? (
                        <div className="flex items-center text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full text-sm font-medium">
                            <Loader2 className="w-4 h-4 animate-spin me-2" />
                            {t('downloadingPdf', {defaultValue: "Generating PDF..."})}
                        </div>
                    ) : (
                         <span className="text-sm text-gray-500 font-medium">Preview Mode</span>
                    )}
                </div>
                <button 
                    onClick={generatePDF}
                    disabled={isGeneratingPdf}
                    className="flex items-center gap-2 bg-primary-600 text-white px-5 py-2.5 rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-sm hover:shadow disabled:opacity-70"
                >
                    <Download className="w-4 h-4" />
                    {t('downloadInvoice')}
                </button>
            </div>

            {/* Render the template */}
            <div className="shadow-2xl">
                <InvoiceTemplate ref={invoiceRef} order={order} client={client} />
            </div>
        </div>
    );
};

export default InvoicePage;
